import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { usePollUntilPendingBrandPlanSchedules } from 'hooks/queries/schedules/usePollUntilPendingBrandPlanSchedules'
import { useMutation, useQueryClient } from 'react-query'
import { ApiError, reportError } from 'utils/error'
import { scheduleKeys } from '../schedules/scheduleKeys'

export type ContentScope = 'current_batch' | 'user_selected_content' | 'upcoming_week_content'

/** Omitted keys fall back to schedule content on the server. */
export interface RegenerateContentOverride {
  file_id: string
  topic?: string
  media_files?: { id: string }[]
  sources?: { type: string; id: string; save_media?: boolean }[]
}

export interface RegenerateBody {
  topic?: string
  file_ids?: string[]
  initial_media_file_ids?: string[]
  content_scope?: ContentScope
  contents?: RegenerateContentOverride[]
}

const regenerateContent = async ({
  workspaceId,
  brandPlanId,
  body,
}: {
  workspaceId: number
  brandPlanId: number
  body: RegenerateBody
}) => {
  const res = await sendToApi(`/w/${workspaceId}/brand_plans/${brandPlanId}/regenerate_content`, {
    method: 'POST',
    body,
  })
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    const errorMessage =
      Array.isArray(res.data?.errors) && typeof res.data.errors[0] === 'string'
        ? res.data.errors.join('. ')
        : 'An error occurred! Our team has been notified and we are looking into it.'
    throw new ApiError(errorMessage, res.data?.error_code)
  }
  return res.data
}

export type RegenerateBrandPlanContentResponse = {
  success: boolean
  regenerated_count: number
  message: string
  content_ids?: number[]
  schedule_id?: number
}

interface UseRegenerateBrandPlanContentOptions {
  onError?: (error: Error) => void
  onSuccess?: (data: RegenerateBrandPlanContentResponse) => void
  onSettled?: () => void
}

export const useRegenerateBrandPlanContent = (options?: UseRegenerateBrandPlanContentOptions) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const pollUntilPendingSchedules = usePollUntilPendingBrandPlanSchedules({})

  return useMutation<RegenerateBrandPlanContentResponse, Error, { brandPlanId: number; body: RegenerateBody }>(
    ({ brandPlanId, body }) => regenerateContent({ workspaceId, brandPlanId, body }),
    {
      onSuccess: (data) => {
        if (data.schedule_id) {
          queryClient.removeQueries(scheduleKeys.detail(data.schedule_id))
        }
        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        pollUntilPendingSchedules()
        options?.onSuccess?.(data)
      },
      onError: (error) => {
        reportError(error)
        options?.onError?.(error)
      },
      onSettled: options?.onSettled,
    },
  )
}
